import React, { useState } from "react";
import { ChevronDown, ChevronRight, Calendar } from "lucide-react";

export const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");
const PAYMENT_TYPE_LABEL = { subscription: "Subscription", credits: "AI credits" };
const STATUS_STYLE = {
  paid: "bg-emerald-500/15 text-emerald-300",
  pending: "bg-amber-500/15 text-amber-300",
  held: "bg-sky-500/15 text-sky-300",
  partial: "bg-amber-500/15 text-amber-300",
};

/** One period's line-item breakdown + running totals — shared by the affiliate
 * portal's Payroll section and the Platform Console's admin matching view. */
export function LineItemsTable({ lineItems, totals }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-[13px] min-w-[760px]">
        <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-white/[0.02]">
          <tr>
            <th className="px-4 py-2.5 font-semibold">Customer</th>
            <th className="px-3 py-2.5 font-semibold">Type</th>
            <th className="px-3 py-2.5 font-semibold text-right">Gross</th>
            <th className="px-3 py-2.5 font-semibold text-right">Processing fee</th>
            <th className="px-3 py-2.5 font-semibold text-right">Net</th>
            <th className="px-3 py-2.5 font-semibold text-right">Rate</th>
            <th className="px-3 py-2.5 font-semibold text-right">Commission</th>
            <th className="px-4 py-2.5 font-semibold text-center">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {(!lineItems || lineItems.length === 0) ? (
            <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-500">No transactions in this period.</td></tr>
          ) : lineItems.map((li) => (
            <tr key={li.id} className="text-slate-200" data-testid={`payroll-line-${li.id}`}>
              <td className="px-4 py-2.5">
                <div className="font-medium">{li.customerName}</div>
                {li.splitLabel && (
                  <div className="text-[11px] text-amber-300/90 font-semibold">
                    {li.splitLabel}{li.splitLabel === "Annual Balance" && li.scheduledPayoutDate ? ` · scheduled ${fmtDate(li.scheduledPayoutDate)}` : ""}
                  </div>
                )}
              </td>
              <td className="px-3 py-2.5 text-slate-400">{PAYMENT_TYPE_LABEL[li.paymentType] || li.paymentType}</td>
              <td className="px-3 py-2.5 text-right tabular-nums text-slate-400">{money(li.grossAmount)}</td>
              <td className="px-3 py-2.5 text-right tabular-nums text-rose-300/80">-{money(li.processingFee)}</td>
              <td className="px-3 py-2.5 text-right tabular-nums text-slate-300">{money(li.netAmount)}</td>
              <td className="px-3 py-2.5 text-right tabular-nums text-slate-400">{li.commissionRate}%</td>
              <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-emerald-300">{money(li.commissionAmount)}</td>
              <td className="px-4 py-2.5 text-center">
                <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${STATUS_STYLE[li.status] || "bg-white/5 text-slate-400"}`}>{li.status}</span>
              </td>
            </tr>
          ))}
        </tbody>
        {totals && (
          <tfoot>
            <tr className="border-t border-white/10 font-semibold text-slate-100" data-testid="payroll-totals-row">
              <td className="px-4 py-3">Total</td>
              <td className="px-3 py-3 text-slate-400">{totals.customerCount} customer{totals.customerCount === 1 ? "" : "s"}</td>
              <td className="px-3 py-3 text-right tabular-nums">{money(totals.grossRevenue)}</td>
              <td className="px-3 py-3 text-right tabular-nums text-rose-300/80">-{money(totals.processingFees)}</td>
              <td className="px-3 py-3"></td>
              <td className="px-3 py-3"></td>
              <td className="px-3 py-3 text-right tabular-nums text-emerald-300">{money(totals.commissionOwed)}</td>
              <td className="px-4 py-3"></td>
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

export function PendingPeriodCard({ period, testid = "payroll-current-period" }) {
  if (!period) return null;
  return (
    <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] overflow-hidden" data-testid={testid}>
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-white/10">
        <Calendar className="h-4 w-4 text-amber-400" />
        <h3 className="text-[14px] font-bold text-slate-100">Current pending payroll</h3>
        <span className="text-[12px] text-slate-400">{fmtDate(period.start)} – {fmtDate(period.end)} · paid out {fmtDate(period.payoutFriday)}</span>
      </div>
      <LineItemsTable lineItems={period.lineItems} totals={period.totals} />
    </div>
  );
}

export function PayrollHistoryList({ history, testid = "payroll-history" }) {
  const [openFriday, setOpenFriday] = useState(null);
  if (!history || history.length === 0) {
    return <div className="rounded-xl border border-white/10 bg-white/[0.02] px-5 py-8 text-center text-slate-500 text-[13px]" data-testid={testid}>No completed payroll periods yet.</div>;
  }
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid={testid}>
      <div className="px-5 py-3.5 border-b border-white/10"><h3 className="text-[14px] font-bold text-slate-100">Payroll history</h3></div>
      <div className="divide-y divide-white/5">
        {history.map((p) => {
          const open = openFriday === p.payoutFriday;
          return (
            <div key={p.payoutFriday}>
              <button
                onClick={() => setOpenFriday(open ? null : p.payoutFriday)}
                data-testid={`payroll-history-row-${p.payoutFriday}`}
                className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-white/[0.02]"
              >
                <div className="flex items-center gap-2">
                  {open ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                  <span className="text-[13px] font-semibold text-slate-200">Paid {fmtDate(p.payoutFriday)}</span>
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize ${STATUS_STYLE[p.status] || "bg-white/5 text-slate-400"}`}>{p.status}</span>
                </div>
                <div className="flex items-center gap-4 text-[12.5px] text-slate-400">
                  <span>{p.totals.customerCount} customers</span>
                  <span>{money(p.totals.grossRevenue)} gross</span>
                  <span className="text-emerald-300 font-semibold">{money(p.totals.commissionOwed)} commission</span>
                </div>
              </button>
              {open && <div className="px-2 pb-3"><LineItemsTable lineItems={p.lineItems} totals={p.totals} /></div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const CUSTOMER_STATUS_STYLE = {
  active: "bg-emerald-500/15 text-emerald-300",
  pending: "bg-amber-500/15 text-amber-300",
  inactive: "bg-white/5 text-slate-500",
};
const CUSTOMER_STATUS_LABEL = { active: "Active", pending: "Pending", inactive: "Inactive" };

export function CustomerStatusTable({ customers, testid = "affiliate-customers-table" }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid={testid}>
      <div className="px-5 py-3.5 border-b border-white/10"><h3 className="text-[14px] font-bold text-slate-100">Referred customers</h3></div>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-[13px] min-w-[560px]">
          <thead className="text-[11px] uppercase tracking-wider text-slate-500 bg-white/[0.02]">
            <tr>
              <th className="px-5 py-2.5 font-semibold">Customer</th>
              <th className="px-3 py-2.5 font-semibold">Signup date</th>
              <th className="px-3 py-2.5 font-semibold text-center">Status</th>
              <th className="px-5 py-2.5 font-semibold text-right">Commission earned</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {(!customers || customers.length === 0) ? (
              <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-500">No referrals yet.</td></tr>
            ) : customers.map((c) => (
              <tr key={c.id} data-testid={`affiliate-customer-row-${c.id}`}>
                <td className="px-5 py-3 font-medium text-slate-200">{c.name}</td>
                <td className="px-3 py-3 text-slate-400">{fmtDate(c.signupDate)}</td>
                <td className="px-3 py-3 text-center">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold ${CUSTOMER_STATUS_STYLE[c.status] || "bg-white/5 text-slate-400"}`}>{CUSTOMER_STATUS_LABEL[c.status] || c.status}</span>
                </td>
                <td className="px-5 py-3 text-right tabular-nums font-semibold text-emerald-300">{money(c.totalCommissionEarned)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
